import React, { FC, ReactNode } from "react";
import classNames from "classnames";

/**
 * Empty · loading · error, in one vocabulary, per design §04.
 *
 * TOKEN SUBSTITUTIONS (the handoff specified literals with no token behind them):
 *   dashed border  #DCDAD6 ("--border-strong") -> `--track`, the existing
 *                  one-step-darker neutral. No new token minted.
 *   inset fill     #fbfbfa                     -> `--surface-2`, per the
 *                  handoff's resolved token table. Reads as an inset well
 *                  inside a white panel and in dark alike.
 *   skeleton fill  "--surface-2, 1.4s shimmer" -> `--track` + Tailwind
 *                  `animate-pulse`, matching the skeletons already shipped in
 *                  VideoSection so the app has one loading idiom, not two.
 */

const BLOCK =
  "flex flex-col items-center justify-center gap-1.5 text-center rounded-xl border border-dashed border-track bg-surface-2 px-[18px] py-[18px]";

interface BlockProps {
  icon: ReactNode;
  title: string;
  body: string;
  /** The chart well is taller than an asset panel's. Applies from `sm` up. */
  minHeight?: number;
  action?: ReactNode;
  /** Stretch to fill a `fill` panel so sibling panels stay level. */
  grow?: boolean;
}

/**
 * `grow` means "match my siblings", and the siblings only exist from `sm` up:
 * below it the three asset panels are stacked one per row, so stretching had
 * nothing to match and just inflated two lines of copy into a ~420px void
 * (design §11.8). So `grow` — and the taller floor that only makes sense
 * alongside it — are both gated on `sm`, leaving a flat 120px on mobile.
 * `minHeight` has to ride a custom property to get there: it is a caller-
 * supplied number, and an inline style cannot carry a media query.
 */
const Block: FC<BlockProps> = ({ icon, title, body, minHeight = 176, action, grow }) => (
  <div
    className={classNames(BLOCK, grow && "min-h-[120px] sm:min-h-[var(--block-min-h)] sm:flex-1")}
    style={grow ? ({ "--block-min-h": `${minHeight}px` } as React.CSSProperties) : { minHeight }}
  >
    <span className="text-muted">{icon}</span>
    <div className="font-CircularStdBlack text-text-1" style={{ fontSize: 13.5 }}>
      {title}
    </div>
    <p className="m-0 text-text-2" style={{ fontSize: 12, lineHeight: 1.4 }}>
      {body}
    </p>
    {action}
  </div>
);

export const PanelEmpty: FC<Omit<BlockProps, "action">> = (props) => <Block {...props} />;

interface PanelErrorProps extends Omit<BlockProps, "action" | "title" | "body"> {
  /** "Couldn't load thumbnails" — name the thing, not the failure. */
  title: string;
  body?: string;
  onRetry?: () => void;
}

/**
 * An outage must never render as "no data". This is the state the page has
 * never had: today the analytics calls swallow their failures in empty catch
 * blocks and fall through to the empty copy, so a total outage and a video
 * nobody watched look identical.
 */
export const PanelError: FC<PanelErrorProps> = ({
  title,
  body = "Something went wrong on our end.",
  onRetry,
  ...rest
}) => (
  <Block
    {...rest}
    title={title}
    body={body}
    action={
      onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 inline-flex items-center h-8 px-3.5 rounded-full bg-surface-2 text-text-1 hover:bg-track transition-colors"
          style={{ fontSize: 12.5 }}
        >
          Try again
        </button>
      ) : undefined
    }
  />
);

const Shimmer: FC<{ className?: string; style?: React.CSSProperties }> = ({
  className,
  style,
}) => <div className={classNames("bg-track animate-pulse motion-reduce:animate-none", className)} style={style} />;

/**
 * Skeletons in the REAL tile geometry. This is the state Thumbnails and
 * Transcripts were missing entirely: their files are discovered by probing S3
 * URLs, which takes seconds, and until it resolved the list was `[]` — so the
 * panel rendered its EMPTY state and then silently filled in. An empty-looking
 * panel could always have been a loading one.
 */
export const PanelSkeleton: FC<{ variant: "tiles" | "rows" | "stories"; count?: number }> = ({
  variant,
  count = variant === "rows" ? 3 : 4,
}) => {
  const items = Array.from({ length: count }, (_, i) => i);

  if (variant === "rows") {
    return (
      <div className="flex flex-col gap-2.5">
        {items.map((i) => (
          <div
            key={i}
            className="flex items-center gap-[11px] border border-border-token rounded-xl px-3"
            style={{ height: 52 }}
          >
            <Shimmer className="rounded-lg shrink-0" style={{ width: 30, height: 30 }} />
            <div className="flex-1 min-w-0 flex flex-col gap-1.5">
              <Shimmer className="rounded" style={{ width: "45%", height: 11 }} />
              <Shimmer className="rounded" style={{ width: "28%", height: 9 }} />
            </div>
            <Shimmer className="rounded-lg shrink-0" style={{ width: 28, height: 28 }} />
          </div>
        ))}
      </div>
    );
  }

  if (variant === "stories") {
    return (
      <div className="flex flex-col gap-3">
        {items.slice(0, 2).map((i) => (
          <div key={i} className="border border-border-token rounded-xl p-2.5">
            <Shimmer className="rounded-lg w-full" style={{ height: 96 }} />
            <Shimmer className="rounded mt-[9px]" style={{ width: "60%", height: 11 }} />
            <Shimmer className="rounded mt-1" style={{ width: "40%", height: 9 }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }}>
      {items.map((i) => (
        <div key={i}>
          <Shimmer className="rounded-lg w-full" style={{ aspectRatio: "16 / 9" }} />
          <Shimmer className="rounded mt-1.5" style={{ width: "70%", height: 10 }} />
        </div>
      ))}
    </div>
  );
};
